import { useCallback, useMemo } from 'react';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useQuery } from 'src/shared/lib/useQuery';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { getIntegrationById, isInbox } from 'src/shared/lib/integration.utils';
import { genQueryKey } from 'src/shared/lib/query.utils';
import {
  Integration,
  IntegrationId,
  IntegrationTypes,
  IntegrationTypesWithFaker,
} from 'src/shared/types/integration.types';
import { QueryResult } from 'src/shared/types/util.types';

type Props = {
  id?: IntegrationId;
  ids?: IntegrationId[];
  group?: string;
  type?: IntegrationTypes;
  isInbox?: boolean;
  refetchOnMount?: boolean | 'always';
};

type ExtraProps = {
  groups: Map<string, Set<IntegrationId>> | undefined;
  integrationMap?: Map<IntegrationId, Integration>;
};

export function useIntegrations<T extends IntegrationTypesWithFaker = IntegrationTypesWithFaker>(): QueryResult<
  Integration<T>[]
> &
  ExtraProps;
export function useIntegrations<T extends IntegrationTypesWithFaker = IntegrationTypesWithFaker>({
  id,
  refetchOnMount,
}: {
  id: IntegrationId | undefined;
  refetchOnMount?: boolean | 'always';
}): QueryResult<Integration<T>> & ExtraProps;
export function useIntegrations<T extends IntegrationTypesWithFaker = IntegrationTypesWithFaker>({
  ids,
}: {
  ids: IntegrationId[];
}): QueryResult<Integration<T>[]> & ExtraProps;
export function useIntegrations<T extends IntegrationTypesWithFaker = IntegrationTypesWithFaker>({
  group,
}: {
  group: string;
}): QueryResult<Integration<T>[]> & ExtraProps;
export function useIntegrations<T extends IntegrationTypesWithFaker = IntegrationTypesWithFaker>({
  isInbox,
}: {
  isInbox: boolean;
}): QueryResult<Integration<T>[]> & ExtraProps;
export function useIntegrations<T extends IntegrationTypesWithFaker = IntegrationTypesWithFaker>({
  group,
  ids,
}: {
  group: string | undefined;
  ids: IntegrationId[] | undefined;
}): QueryResult<Integration<T>[]> & ExtraProps;
export function useIntegrations<T extends IntegrationTypesWithFaker = IntegrationTypesWithFaker>({
  type,
  group,
  ids,
}: {
  type: IntegrationTypes;
  group: string;
  ids: IntegrationId[];
}): QueryResult<Integration<T>[]> & ExtraProps;
export function useIntegrations<T extends IntegrationTypesWithFaker = IntegrationTypesWithFaker>(
  options: Props = {}
): QueryResult<Integration<T>[] | Integration<T>> & ExtraProps {
  const { orgFetch, currentOrg } = useOrg();
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => ['integrations', genQueryKey(currentOrg.id)], [currentOrg.id]);

  const integrationId = options?.id;
  const integrationGroup = options?.group;
  const integrationIds = options?.ids?.join(',');
  const integrationType = options?.type;
  const integrationIsInbox = options?.isInbox;

  const select = useCallback(
    (integrations: Integration[]) => {
      if (integrationId !== undefined) {
        return getIntegrationById(integrationId, integrations);
      }
      if (integrationGroup) {
        return integrations.filter((integration) => integration.groups.includes(integrationGroup));
      }
      if (integrationIds) {
        return integrations.filter((integration) => integrationIds?.includes(integration.id));
      }
      if (integrationType) {
        return integrations.filter((integration) => integration.type === integrationType);
      }
      if (integrationIsInbox !== undefined) {
        return integrations.filter((integration) => isInbox(integration.type));
      }
      return integrations;
    },
    [integrationId, integrationGroup, integrationIds, integrationType, integrationIsInbox]
  );

  const query = useQuery({
    queryKey: queryKey,
    queryFn: getData,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: options.refetchOnMount,
    placeholderData: keepPreviousData,
    select,
  });

  async function getData() {
    const res = await orgFetch<Integration[]>(`/api/v2/integrations`);
    if (!res.ok) {
      throw new Error('Failed to fetch integrations');
    }
    const data = await res.json();

    return data.sort((a, b) => {
      if (!a?.name) {
        return 1;
      }
      if (!b?.name) {
        return -1;
      }
      return a.name?.localeCompare(b.name);
    });
  }

  const { groups, integrationMap } = useMemo(() => {
    const groups: Map<string, Set<IntegrationId>> = new Map();
    const integrationMap = new Map<IntegrationId, Integration>();
    queryClient.getQueryData<Integration[]>(queryKey)?.forEach((integration) => {
      integrationMap.set(integration.id, integration);
      integration.groups.forEach((group) => {
        if (!groups.has(group)) {
          groups.set(group, new Set<IntegrationId>());
        }
        groups.get(group)?.add(integration.id);
      });
    });

    return { groups, integrationMap };
  }, [query.dataUpdatedAt, queryClient, queryKey]);

  return {
    ...query,
    groups,
    integrationMap,
  } as QueryResult<Integration<T>[] | Integration<T>> & ExtraProps;
}
